import { Select, Space, Tag, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePermissions } from '@/shared/auth/usePermissions'
import { fetchWarehouses } from './stockApi'

interface WarehouseSelectProps {
  /** antd `Form.Item` o'zi uzatadi — qo'lda berilmaydi */
  value?: string | null
  onChange?: (value: string | null) => void
  disabled?: boolean
  /**
   * Tanlovni tozalash mumkinmi.
   *
   * ⚠️ Tahrirlashda `false` bo'lishi SHART va sabab kontraktda:
   * `EquipmentRepository#updateDetails` da `node_id = COALESCE(:nodeId,
   * node_id)`, ya'ni `null` "tozala" emas, "TEGMA" degani. Tozalash
   * tugmasini chizsak, operator xochni bosardi, "Saqlash" muvaffaqiyatli
   * o'tardi va ombor JOYIDA qolardi — panel bajarilmagan ishni
   * bajarilgandek ko'rsatgan bo'lardi.
   */
  allowClear?: boolean
}

/**
 * Ombor (tashkiliy tugun) tanlash — jihoz formasida ishlatiladi.
 *
 * <h2>⚠️ RO'YXAT NOMGA QARAB FILTRLANMAYDI (§19.3)</h2>
 * Bu yerda `kind === 'WAREHOUSE'` degan shart YO'Q va bo'lmaydi ham.
 * Ombor ekanini turning IMKONIYATI aytadi (`holds_stock`), NOMI emas:
 * administrator "Markaziy sklad" degan yangi tur ochib `holds_stock =
 * true` qo'ysa, u shu ro'yxatda darhol paydo bo'lishi kerak. Nom
 * bo'yicha filtr §19.3 ni buzardi va uni backendda
 * `OrgNodeTypeGuardTest` ushlaydi.
 *
 * Server ro'yxatni ALLAQACHON ikki marta filtrlab beradi — imkoniyat
 * (`holds_stock`) va ko'rinish doirasi bo'yicha (`fetchWarehouses`
 * javadoc'i). Panel uchinchi filtr o'ylab topmaydi.
 *
 * <h2>⚠️ YOPILGAN OMBOR RO'YXATDA QOLADI, lekin belgilangan holda</h2>
 * `SiteSelect` bilan bir xil qaror: yopilgan omborda jihoz turgan
 * bo'lishi mumkin va uni tahrirlashda jimgina yo'qotib bo'lmaydi.
 * Yangi biriktirish uchun esa u ko'rinib turgan tanlov — operator
 * ataylab tanlaydi.
 *
 * ⚠️ `canReceiveStock` bu yerda QO'LLANMAYDI. U qoldiq HARAKATI uchun
 * (o'lik tugunda yangi qoldiq hosil qilmaslik), jihoz esa allaqachon
 * fizik jihatdan o'sha omborda turibdi — uni ro'yxatdan olib tashlash
 * mavjud holatni yozib bo'lmaydigan qilardi.
 *
 * <h2>⚠️ BO'SH RO'YXAT — XULOSA, uni faqat javob berardi</h2>
 * "Ombor yo'q" degani `isSuccess` da, `!isPending` da EMAS: yiqilgan
 * so'rovda ham ro'yxat bo'sh bo'ladi va o'sha yozuv ISHONCHLI javob
 * bo'lib o'qilardi (`admin/tests/guards.mjs` §3 dagi
 * `StockBalancesPage` nuqsoni).
 *
 * <h2>⚠️ HUQUQ IKKI XIL — o'lchangan tuzoq</h2>
 * Jihoz yaratish `inventory.equipment.write`, ombor ro'yxati esa
 * `inventory.stock.read` talab qiladi (`StockResource#warehouses`).
 * Seedda `ADMIN` da birinchisi BOR, ikkinchisi YO'Q
 * (`V202608131600__identity_inventory_stock_permissions.sql`: uchala
 * yangi huquq faqat `SUPERADMIN` va `VIEWER` ga tushgan). Ya'ni
 * so'rovni shartsiz yuborsak, `ADMIN` har forma ochganda 403 olardi va
 * maydon "ombor yo'q" bo'lib ko'rinardi. Shuning uchun so'rov huquq
 * bo'lganda yuboriladi, aks holda sabab OCHIQ yoziladi.
 */
export function WarehouseSelect({
  value,
  onChange,
  disabled,
  allowClear = false,
}: WarehouseSelectProps) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const allowed = can('inventory.stock.read')

  const query = useQuery({
    queryKey: queryKeys.stock.warehouses,
    queryFn: fetchWarehouses,
    enabled: allowed,
    staleTime: 5 * 60_000,
  })

  const warehouses = query.data ?? []

  const options = warehouses.map((warehouse) => ({
    value: warehouse.id,
    label: (
      <Space size={4}>
        <span>{warehouse.displayName}</span>
        <Typography.Text type="secondary">{warehouse.code}</Typography.Text>
        {warehouse.state === 'CLOSED' ? <Tag color="default">{t('orgNodeState.CLOSED')}</Tag> : null}
      </Space>
    ),
    // Qidiruv NOM va KOD bo'yicha: bir filialda bir nechta ombor bo'lganda
    // faqat nom bo'yicha izlash yetmaydi
    title: `${warehouse.displayName} ${warehouse.code}`,
  }))

  // Tanlangan ombor ro'yxatda yo'q (huquq yo'q, doiradan tashqarida yoki
  // yopilgan) — uni JIMGINA tashlab yuborish mumkin emas. Qiymat forma
  // maydonida o'z holicha turadi va saqlashda qaytariladi; bu yozuv esa
  // operatorga NEGA nom ko'rinmayotganini aytadi.
  const missing = value !== null && value !== undefined && value !== '' && !warehouses.some((w) => w.id === value)
  const shownOptions = missing
    ? [...options, { value, label: <>{t('inventory.warehouseUnknown')}</>, title: '', disabled: true }]
    : options

  const notFound = allowed
    ? query.isSuccess
      ? t('stock.field.noWarehouses')
      : t('inventory.warehouseUnavailable')
    : t('inventory.warehouseNoPermission')

  return (
    <>
      <Select<string | null>
        allowClear={allowClear}
        showSearch
        value={value ?? null}
        onChange={(next) => onChange?.(next ?? null)}
        disabled={disabled}
        loading={query.isPending && allowed}
        placeholder={t('inventory.warehousePlaceholder')}
        options={shownOptions}
        optionFilterProp="title"
        style={{ width: '100%' }}
        notFoundContent={notFound}
      />
      {/* Yiqilgan so'rov "ombor yo'q" bo'lib ko'rinmasin — sabab yozib
          qo'yiladi, aks holda operator ro'yxat haqiqatan bo'sh deb o'ylardi */}
      {allowed && query.isError ? (
        <Typography.Text type="danger" style={{ fontSize: 12 }}>
          {t('inventory.warehouseUnavailable')}
        </Typography.Text>
      ) : null}
      {!allowed ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t('inventory.warehouseNoPermission')}
        </Typography.Text>
      ) : null}
      {missing ? (
        <Typography.Text type="warning" style={{ fontSize: 12 }}>
          {t('inventory.warehouseMissing')}
        </Typography.Text>
      ) : null}
    </>
  )
}
